import React, {useState, useMemo, useRef, useEffect} from 'react';
import './purchase-analysis-components.css';
import {RangoFechas} from '../../../../helpers/datesHelper';
import axios from "axios";
import {UpdateCostoDesdeUltimoCosto} from "../../../../services/purchaseServices";
import {CalcularPrecioVenta, GetProducto, PutProducto} from "../../../../services/productosServices";
import { GetProveedores } from "../../../../services/proveedoresServices";
// Credenciales Google: https://console.cloud.google.com/apis/credentials?project=todoamipuerta

// --- Child Components ---
const SummaryCard = ({title, value, isBad = false, tooltip = ''}) => (
    <div title={tooltip} className="summary-card">
        <h3>{title}</h3>
        <p className={isBad ? 'bad-value' : ''}>{value}</p>
    </div>
);

// Nuevo Card específico para las variaciones
const VariationSummaryCard = ({title, total, positive, negative, tooltip = ''}) => (
    <div title={tooltip} className="summary-card variation-card">
        <h3>{title}</h3>
        <p className="bad-value">{total}</p>
        <div className="variation-split">
            <span className="positive">Ahorro: {negative}</span>
            <span className="negative">Exceso: {positive}</span>
        </div>
    </div>
);


const AnalysisSection = ({summary, processedData, filteredData, groupBy}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const chatEndRef = useRef(null);
    const [isCopying, setIsCopying] = useState(false);
    const [copyOk, setCopyOk] = useState(false);

    // Effect to scroll to the bottom of the chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [chatHistory]);


    const callGeminiAPI = async (currentChatHistory) => {
        const topAggregatedVariations = processedData.isGrouped
            ? processedData.items.slice(0, 5).map(g => `- ${g.key}: ${formatCurrency(g.totalAbsoluto)}`).join('\n')
            : 'N/A (vista no agrupada)';

        const failedDeliveries = filteredData
            .filter(item => item.importeSolicitado > 0 && item.importeRecibido === 0)
            .slice(0, 5)
            .map(item => `- Producto: ${item.producto}, Proveedor: ${item.proveedor}, Monto: ${formatCurrency(item.importeSolicitado)}`)
            .join('\n');

        const topIndividualVariations = filteredData
            .sort((a, b) => Math.abs(b.DiferenciaImporte) - Math.abs(a.DiferenciaImporte))
            .slice(0, 7)
            .map(item => `- Producto: ${item.producto}, Proveedor: ${item.proveedor}, Solicitado: ${formatCurrency(item.importeSolicitado)}, Recibido: ${formatCurrency(item.importeRecibido)}, Diferencia: ${formatCurrency(item.diferenciaImporte)}`)
            .join('\n');

        // The context is now part of the system instruction
        const systemPrompt = `Actúas como un experto analista de compras para un gerente. Tu tarea es analizar datos de compras y conversar sobre ellos. Responde siempre en español.
        
        **Contexto de Datos Actual (para toda la conversación):**
        - Periodo Analizado: Compras del periodo actual.
        - Gasto Real Total: ${formatCurrency(summary.totalRecibido)}
        - Gasto Presupuestado: ${formatCurrency(summary.totalSolicitado)}
        - Variación Absoluta Total: ${formatCurrency(summary.totalAbsoluto)}
        - Top 5 ${groupBy} con Mayor Variación:
        ${topAggregatedVariations}
        - Transacciones con Mayor Impacto:
        ${topIndividualVariations}
        - Pedidos con Fallas de Entrega:
        ${failedDeliveries || 'Ninguna.'}
        
        **Instrucciones de Interacción:**
        1. Para la primera pregunta del usuario, proporciona un análisis gerencial detallado y puntual. El análisis debe incluir: un resumen de hallazgos, 3-4 puntos clave (bullets) y 3 recomendaciones accionables.
        2. Para preguntas de seguimiento, responde de manera concisa y basándote en el contexto de datos proporcionado.
        3. Mantén un tono profesional y directo. Responde siempre en formato Markdown.`;

        try {
            const apiKey = "AIzaSyCOaGoibWRdDjzomYAJ8Q_zoBv6yc2708g"; //TODO: Cambiar a usar ENV
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

            const payload = {
                contents: currentChatHistory,
                systemInstruction: {parts: [{text: systemPrompt}]},
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.statusText}`);
            }
            const result = await response.json();
            const generatedText = result.candidates?.[0]?.content?.parts?.[0]?.text;

            if (generatedText) {
                return generatedText;
            } else {
                throw new Error("No se recibió contenido en la respuesta de la API.");
            }
        } catch (err) {
            console.error("Error calling Gemini API:", err);
            setError("Ocurrió un error al comunicarse con la IA. Por favor, intente de nuevo.");
            return null;
        }
    };

    const generateInitialAnalysis = async () => {
        setLoading(true);
        setError('');
        setChatHistory([]);
        setIsOpen(true);

        const initialPrompt = `Por favor, genera el análisis gerencial inicial basado en el contexto de datos que tienes.`;
        const initialHistory = [{role: 'user', parts: [{text: initialPrompt}]}];
        const modelResponse = await callGeminiAPI(initialHistory);

        if (modelResponse) {
            setChatHistory([...initialHistory, {role: 'model', parts: [{text: modelResponse}]}]);
        }
        setLoading(false);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userInput.trim()) return;

        const newUserMessage = {role: 'user', parts: [{text: userInput}]};
        const updatedChatHistory = [...chatHistory, newUserMessage];
        setChatHistory(updatedChatHistory);
        setUserInput("");
        setLoading(true);

        const modelResponse = await callGeminiAPI(updatedChatHistory);
        if (modelResponse) {
            setChatHistory([...updatedChatHistory, {role: 'model', parts: [{text: modelResponse}]}]);
        }
        setLoading(false);
    };

    const handleCopyChat = async () => {
        try {
            setIsCopying(true);
            setCopyOk(false);
            const text = chatHistory.slice(1).map(m => {
                const role = m.role === 'user' ? 'Usuario' : 'IA';
                const content = (m.parts || []).map(p => p.text || '').join(' ').trim();
                return `${role}: ${content}`;
            }).join('\n\n');

            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopyOk(true);
            setTimeout(() => setCopyOk(false), 2000);
        } catch (e) {
            console.error('Copy failed', e);
            setError('No se pudo copiar la conversación.');
        } finally {
            setIsCopying(false);
        }
    };

    const MarkdownRenderer = ({text}) => {
        if (!text) return null;
        const lines = text.split('\n');
        const elements = [];
        let currentList = [];

        const flushList = () => {
            if (currentList.length > 0) {
                elements.push(<ul key={`ul-${elements.length}`}>{currentList}</ul>);
                currentList = [];
            }
        };

        lines.forEach((line, index) => {
            if (line.startsWith('* ') || line.startsWith('- ')) {
                const formattedLine = line.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                currentList.push(<li key={index} dangerouslySetInnerHTML={{__html: formattedLine}}/>);
                return;
            }

            flushList();

            if (line.startsWith('###') || line.startsWith('####')) {
                elements.push(<h4 key={index}>{line.replace(/#/g, '').trim()}</h4>);
            } else if (line.trim()) {
                const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                elements.push(<p key={index} dangerouslySetInnerHTML={{__html: formattedLine}}/>);
            }
        });

        flushList();
        return <div>{elements}</div>;
    };


    return (
        <div className="analysis-section">
            <div className="analysis-header">
                <button onClick={() => setIsOpen(!isOpen)} className="toggle-analysis">
                    {isOpen ? 'Ocultar' : 'Mostrar'} Análisis Gerencial {isOpen ? '▲' : '▼'}
                </button>
                <div style={{display: 'flex', gap: '8px', paddingRight: '8px'}}>
                    <button onClick={generateInitialAnalysis} disabled={loading} className="generate-button">
                        {loading && chatHistory.length === 0 ? 'Generando...' : 'Generar Análisis con IA'}
                    </button>
                    {chatHistory.length > 0 && (
                        <button onClick={handleCopyChat} disabled={isCopying} className="copy-button">
                            {isCopying ? 'Copiando…' : copyOk ? '¡Copiado!' : 'Copiar conversación'}
                        </button>
                    )}
                </div>
            </div>
            {isOpen && (
                <div className="analysis-content">
                    <div className="chat-container">
                        {chatHistory.slice(1).map((msg, index) => ( // slice(1) to hide the initial prompt
                            <div key={index} className={`chat-message ${msg.role}-message`}>
                                <MarkdownRenderer text={msg.parts[0].text}/>
                            </div>
                        ))}
                        {loading && <div className="chat-message model-message">...</div>}
                        <div ref={chatEndRef}/>
                    </div>
                    {error && <p className="negative">{error}</p>}
                    {chatHistory.length > 0 && (
                        <form onSubmit={handleSendMessage} className="chat-input-area">
                            <input
                                type="text"
                                className="chat-input"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                placeholder="Haz una pregunta sobre el análisis..."
                                disabled={loading}
                            />
                            <button type="submit" className="send-button" disabled={loading}>
                                Enviar
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};
const HistoricalPriceAnalysis = ({currentData, historicalData}) => {
    const analysis = useMemo(() => {
        const productPrices = currentData.reduce((acc, item) => {
            if (item.cantidadRecibida > 0) {
                if (!acc[item.producto]) {
                    acc[item.producto] = {totalPrecio: 0, count: 0};
                }
                acc[item.producto].totalPrecio += item.precioRecibido;
                acc[item.producto].count += 1;
            }
            return acc;
        }, {});

        return historicalData.map(histItem => {
            const current = productPrices[histItem.producto];
            if (!current) return null;

            const avgCurrentPrice = current.totalPrecio / current.count;
            const variation = ((avgCurrentPrice - histItem.precioHistorico) / histItem.precioHistorico) * 100;

            return {
                ...histItem,
                PrecioActual: avgCurrentPrice,
                Variacion: variation,
            };
        }).filter(Boolean).sort((a, b) => Math.abs(b.Variacion) - Math.abs(a.Variacion));

    }, [currentData, historicalData]);

    return (
        <div className="historical-analysis-card">
            <h4>Variación de Precio vs. Histórico (3 meses)</h4>
            <p className="subtitle">Productos con mayores cambios de precio.</p>
            <table>
                <thead>
                <tr>
                    <th>Producto</th>
                    <th>Costo Actual (Prom.)</th>
                    <th>Costo Histórico</th>
                    <th>Variación</th>
                    <th>Precio venta actual</th>
                    <th>Margen</th>
                </tr>
                </thead>
                <tbody>
                {analysis.map(item => (
                    <tr key={item.producto}>
                        <td data-label="Producto">{item.producto}</td>
                        <td data-label="Precio Actual (Prom.)"
                            className="amount">{formatCurrency(item.costoActual)}</td>
                        <td data-label="Precio Histórico" className="amount">{formatCurrency(item.costoHistorico)}</td>
                        <td data-label="Variación"
                            className={`amount ${item.variacionPorcentual} < 0 ? 'negative' : item.variacionPorcentual > 5 ? 'positive' : ''}`}>
                            {item.variacionPorcentual.toFixed(1)}%
                        </td>
                        <td data-label="Precio venta actual"
                            className="amount">{formatCurrency(item.precioVentaActual)}</td>
                        <td data-label="Margen"
                            className={`amount ${item.margenPorcentual > 5 ? 'negative' : item.margen < -5 ? 'positive' : ''}`}>
                            {item.margenPorcentual.toFixed(1)}%
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};


const ProductMarginAnalysis = ({productMarginData, searchTerm}) => {
    const [localPMData, setLocalPMData] = useState(productMarginData || []);

    // Modal state for editing
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editTargetId, setEditTargetId] = useState(null);
    const [editForm, setEditForm] = useState({precioVentaActual: '', costoRegistrado: ''});

    // Proveedor change modal state
    const [isProveedorOpen, setIsProveedorOpen] = useState(false);
    const [proveedorTargetId, setProveedorTargetId] = useState(null);
    const [proveedorProducto, setProveedorProducto] = useState(null);
    const [proveedoresList, setProveedoresList] = useState([]);
    const [selectedProveedorId, setSelectedProveedorId] = useState(null);
    const [savingProveedor, setSavingProveedor] = useState(false);

    // Keep local copy in sync when prop changes
    useEffect(() => {
        setLocalPMData(productMarginData || []);
    }, [productMarginData]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [pmSort, setPmSort] = useState({key: null, direction: 'asc'});

    // Reset page on data, page size, or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [localPMData, rowsPerPage, searchTerm]);

    // Apply search filter by product name (case-insensitive)
    const filtered = useMemo(() => {
        if (!localPMData) return [];
        const term = (searchTerm || '').trim().toLowerCase();
        if (!term) return localPMData;
        return localPMData.filter(p => (p.producto || '').toLowerCase().includes(term));
    }, [localPMData, searchTerm]);

    // Sorting helpers for Product Margin table
    const parseDate = (v) => {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.getTime();
    };
    const getVal = (obj, key) => {
        if (!obj) return null;
        const val = obj[key];
        if (key === 'fechaUltimaCompra') return parseDate(val);
        return val;
    };
    const compare = (a, b, key, direction) => {
        const va = getVal(a, key);
        const vb = getVal(b, key);
        let res = 0;
        if (typeof va === 'number' && typeof vb === 'number') {
            res = va - vb;
        } else if (typeof va === 'string' && typeof vb === 'string') {
            res = va.localeCompare(vb, 'es', {numeric: true, sensitivity: 'base'});
        } else if (va == null && vb != null) {
            res = -1;
        } else if (va != null && vb == null) {
            res = 1;
        } else {
            res = (va > vb) - (va < vb);
        }
        return direction === 'asc' ? res : -res;
    };

    const toggleSort = (key) => {
        setPmSort(prev => {
            if (prev.key === key) {
                return {key, direction: prev.direction === 'asc' ? 'desc' : 'asc'};
            }
            return {key, direction: 'asc'};
        });
    };

    const sortedFiltered = useMemo(() => {
        const items = [...filtered];
        if (pmSort.key) {
            items.sort((a, b) => compare(a, b, pmSort.key, pmSort.direction));
        }
        return items;
    }, [filtered, pmSort]);

    // Proveedor change handlers
    const openProveedorModal = async (item) => {
        if (!item) return;
        try {
            const producto = await GetProducto(item.idProducto);
            const proveedores = await GetProveedores();
            setProveedorProducto(producto);
            setProveedoresList(proveedores || []);
            const initialId = producto?.proveedorId || producto?.proveedor?.id || null;
            setSelectedProveedorId(initialId);
            setProveedorTargetId(item.idProducto);
            setIsProveedorOpen(true);
        } catch (e) {
            console.error('Error al abrir cambio de proveedor', e);
        }
    };

    const closeProveedorModal = () => {
        setIsProveedorOpen(false);
        setProveedorTargetId(null);
        setProveedorProducto(null);
        setProveedoresList([]);
        setSelectedProveedorId(null);
        setSavingProveedor(false);
    };

    const saveProveedor = async () => {
        if (!proveedorTargetId || !proveedorProducto) return closeProveedorModal();
        try {
            setSavingProveedor(true);
            const prod = { ...proveedorProducto };
            const selected = (proveedoresList || []).find(x => x.id === Number(selectedProveedorId));
            prod.proveedorId = selected ? selected.id : null;
            prod.proveedor = selected || null;
            await PutProducto(prod);
            setLocalPMData(prev => prev.map(p => {
                if (p.idProducto !== proveedorTargetId) return p;
                return { ...p, proveedor: selected ? (selected.nombre || selected.razonSocial || selected.name || '') : '' };
            }));
        } catch (e) {
            console.error('Error al guardar proveedor', e);
        } finally {
            setSavingProveedor(false);
            closeProveedorModal();
        }
    };

    // Handlers for edit modal
    const openEdit = async (item) => {
        if (!item) return;

        var producto = await GetProducto(item.idProducto);

        setEditTargetId(item.idProducto);
        setEditForm({
            producto: producto,
            ultimoCostoCompra: Number(item.ultimoCostoCompra ?? 0),
            precioVentaActual: Number(producto.precio ?? 0),
            costoRegistrado: Number(producto.costoUnitario ?? 0),
            margen: Number(producto.margen ?? 0),
            conceptoSobrePrecio1: producto.conceptoSobrePrecio1,
            montoSobrePrecio1: Number(producto.montoSobrePrecio1),
            conceptoSobrePrecio2: producto.conceptoSobrePrecio2,
            montoSobrePrecio2: Number(producto.montoSobrePrecio2),
        });

        setIsEditOpen(true);
    };

    const calcularPrecioVenta = (costoUnitario, margen, montoSobrePrecio1, montoSobrePrecio2) => {
        if (!costoUnitario || !margen) return 0;
        console.log(costoUnitario, margen, montoSobrePrecio1, montoSobrePrecio2);
        return CalcularPrecioVenta({costoUnitario, margen, montoSobrePrecio1, montoSobrePrecio2});
    }

    const closeEdit = () => {
        setIsEditOpen(false);
        setEditTargetId(null);
        setEditForm({precioVentaActual: '', costoRegistrado: ''});
    };

    const saveEdit = async () => {
        if (!editTargetId) return closeEdit();
        const precio = Number(editForm.precioVentaActual) || 0;
        const costo = Number(editForm.costoRegistrado) || 0;
        setLocalPMData(prev => prev.map(p => {
            if (p.idProducto !== editTargetId) return p;
            const utilidad = precio - costo;
            const margenPorcentual = precio ? (utilidad / precio) * 100 : 0;
            return {
                ...p,
                precioVentaActual: precio,
                costoRegistrado: costo,
                utilidad,
                margenPorcentual
            };
        }));

        editForm.producto.precio = precio;
        editForm.producto.costoUnitario = costo;
        editForm.producto.margen = Number(editForm.margen) || 0;
        editForm.producto.montoSobrePrecio1 = Number(editForm.montoSobrePrecio1) || 0;
        editForm.producto.montoSobrePrecio2 = Number(editForm.montoSobrePrecio2) || 0;
        
        await PutProducto(editForm.producto);

        closeEdit();
    };

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const pageData = useMemo(() => {
        if (!sortedFiltered) return [];
        const start = (currentPage - 1) * rowsPerPage;
        return sortedFiltered.slice(start, start + rowsPerPage);
    }, [sortedFiltered, currentPage, rowsPerPage]);

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(totalItems, currentPage * rowsPerPage);

    const SortIcon = ({active, direction}) => (
        <span className="sort-icon" aria-hidden="true">{active ? (direction === 'asc' ? '▲' : '▼') : ''}</span>
    );

    const actualizarDesdeUltimoCosto = async (item) => {
        if (!item || !item.producto) return;
        const {idProducto} = item;
        try {
            const result = await UpdateCostoDesdeUltimoCosto(idProducto);
            // Expecting { nuevoPrecio, nuevoCosto } from API
            const nuevoPrecio = result?.nuevoPrecio ?? result?.precio ?? result?.nuevoPrecioVenta;
            const nuevoCosto = result?.nuevoCosto ?? result?.costo ?? result?.nuevoCostoRegistrado;

            setLocalPMData(prev => prev.map(p => {
                if (p.idProducto !== idProducto) return p;
                const updatedPrecio = (nuevoPrecio ?? p.precioVentaActual);
                const updatedCosto = (nuevoCosto ?? p.costoRegistrado);
                const utilidad = (Number(updatedPrecio) || 0) - (Number(updatedCosto) || 0);
                const margenPorcentual = (Number(updatedPrecio) ? (utilidad / Number(updatedPrecio)) * 100 : 0);
                return {
                    ...p,
                    precioVentaActual: updatedPrecio,
                    costoRegistrado: updatedCosto,
                    utilidad,
                    margenPorcentual
                };
            }));


        } catch (e) {
            console.error('Error al actualizar desde último costo', e);
            // Optional: Add user feedback here (toast/alert) if desired
        }
    }

    return (
        <div className="product-margin-card">
            <h4>Análisis de Rentabilidad de Productos</h4>
            <p className="subtitle">Rentabilidad basada en el costo registrado vs. el último costo de compra.</p>
            {(!localPMData || localPMData.length === 0) &&
                <div>
                    <span colSpan="6">Cargando ...</span>
                </div>
            }
            {localPMData && localPMData.length > 0 && (
                <>
                    {/* Edit Modal */}
                    {isEditOpen && (
                        <div className="modal-overlay" onClick={closeEdit}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <h3>Editar Precio y Costo</h3>
                                <div className="form-row">
                                    <label htmlFor="pv">Último Costo:</label>
                                    <div>
                                        <label htmlFor="pv">{formatCurrency(editForm.ultimoCostoCompra)}</label>
                                        <button className="btn-primary" onClick={() => setEditForm(f => ({
                                            ...f, costoRegistrado: f.ultimoCostoCompra,
                                            precioVentaActual: calcularPrecioVenta(editForm.ultimoCostoCompra, f.margen, f.montoSobrePrecio1, f.montoSobrePrecio2)
                                        }))
                                        }
                                        >Copiar <i className="fa-solid fa-arrow-right"></i></button>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <label htmlFor="cr">Costo:</label>
                                    <input id="cr" type="number" step="0.01" value={editForm.costoRegistrado}
                                           onChange={(e) =>
                                               setEditForm(f => ({
                                                   ...f, costoRegistrado: e.target.value,
                                                   precioVentaActual: calcularPrecioVenta(e.target.value, f.margen, f.montoSobrePrecio1, f.montoSobrePrecio2)
                                               }))}
                                           style={{textAlign: 'right'}}/>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="cr">Margen:</label>
                                    <input id="cr" type="number" step="0.01" value={editForm.margen}
                                           onChange={(e) => setEditForm(f => ({
                                               ...f, margen: e.target.value,
                                               precioVentaActual: calcularPrecioVenta(f.costoRegistrado, e.target.value, f.montoSobrePrecio1, f.montoSobrePrecio2)
                                           }))}
                                           style={{textAlign: 'right'}}/>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="cr">{editForm.conceptoSobrePrecio1 ?? 'SobrePrecio1'}:</label>
                                    <input id="cr" type="number" step="0.01" value={editForm.montoSobrePrecio1}
                                           onChange={(e) => setEditForm(f => ({
                                               ...f, montoSobrePrecio1: e.target.value,
                                               precioVentaActual: calcularPrecioVenta(f.costoRegistrado, f.margen, Number(e.target.value ?? 0), f.montoSobrePrecio2)
                                           }))}
                                           style={{textAlign: 'right'}}/>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="cr">{editForm.conceptoSobrePrecio2 ?? 'SobrePrecio2'}:</label>
                                    <input id="cr" type="number" step="0.01" value={editForm.montoSobrePrecio2}
                                           onChange={(e) => setEditForm(f => ({
                                               ...f, montoSobrePrecio2: e.target.value,
                                               precioVentaActual: calcularPrecioVenta(f.costoRegistrado, f.margen, f.montoSobrePrecio1, Number(e.target.value ?? 0))
                                           }))}
                                           style={{textAlign: 'right'}}/>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="pv">Precio Venta Actual</label>
                                    <label
                                        htmlFor="pv"><strong>{formatCurrency(editForm.precioVentaActual)}</strong></label>
                                </div>

                                <div className="modal-actions">
                                    <button className="btn-secondary" onClick={closeEdit}>Cancelar</button>
                                    <button className="btn-primary" onClick={saveEdit}>Guardar</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cambiar Proveedor Modal */}
                    {isProveedorOpen && (
                        <div className="modal-overlay" onClick={closeProveedorModal}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <h3>Cambiar Proveedor</h3>
                                <div className="form-row">
                                    <label htmlFor="sel-prov">Proveedor:</label>
                                    <select id="sel-prov" className="select-as-button" value={selectedProveedorId || ''}
                                            onChange={(e) => setSelectedProveedorId(e.target.value ? Number(e.target.value) : null)}
                                            aria-label="Seleccionar proveedor">
                                        <option value="">Haga clic para elegir proveedor…</option>
                                        {(proveedoresList || []).map(p => (
                                            <option key={p.id} value={p.id}>{p.nombre || p.razonSocial || p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="modal-actions">
                                    <button className="btn-secondary" onClick={closeProveedorModal} disabled={savingProveedor}>Cancelar</button>
                                    <button className="btn-primary" onClick={saveProveedor} disabled={savingProveedor || selectedProveedorId == null}>
                                        {savingProveedor ? 'Guardando...' : 'Guardar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <table>
                        <thead>
                        <tr>
                            <th className="sortable" onClick={() => toggleSort('producto')}>Producto <SortIcon
                                active={pmSort.key === 'producto'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('precioVentaActual')}>Precio Venta <SortIcon
                                active={pmSort.key === 'precioVentaActual'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('costoRegistrado')}>Costo Registrado <SortIcon
                                active={pmSort.key === 'costoRegistrado'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('ultimoCostoCompra')}>Último Costo Compra <SortIcon
                                active={pmSort.key === 'ultimoCostoCompra'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('fechaUltimaCompra')}>Fecha Últ. Compra <SortIcon
                                active={pmSort.key === 'fechaUltimaCompra'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('utilidad')}>Utilidad <SortIcon
                                active={pmSort.key === 'utilidad'} direction={pmSort.direction}/></th>
                            <th className="sortable" style={{textAlign: "right"}}
                                onClick={() => toggleSort('margenPorcentual')}>Margen % <SortIcon
                                active={pmSort.key === 'margenPorcentual'} direction={pmSort.direction}/></th>
                            <th className="sortable" onClick={() => toggleSort('proveedor')}>Proveedor <SortIcon
                                active={pmSort.key === 'proveedor'} direction={pmSort.direction}/></th>
                        </tr>
                        </thead>
                        <tbody>
                        {pageData.map((item, index) => (
                            <tr key={index}>
                                <td data-label="Producto">{item.producto}</td>
                                <td data-label="Precio Venta"
                                    className="amount"
                                    style={{textAlign: "right"}}>{formatCurrency(item.precioVentaActual)}</td>
                                <td data-label="Costo Registrado"
                                    className="amount"
                                    style={{textAlign: "right"}}>
                                    <div className="table-column-with-button">
                                        <button className="btn-primary btn-sm" onClick={() => openEdit(item)}>
                                            <i className="fa-solid fa-pencil"></i></button>
                                        <span>
                                    {formatCurrency(item.costoRegistrado)}
                                        </span>
                                    </div>

                                </td>
                                <td data-label="Último Costo Compra"
                                    className="amount table-column-with-button" style={{textAlign: "right"}}>
                                    <button className="btn-primary btn-sm" onClick={() => {
                                        actualizarDesdeUltimoCosto(item)
                                    }}><i
                                        className="fa-solid fa-arrow-left"></i></button>
                                    <span>
                                    {formatCurrency(item.ultimoCostoCompra)}</span>
                                </td>
                                <td data-label="Fecha Últ. Compra" className="amount"
                                    style={{textAlign: "right"}}>{(item.fechaUltimaCompra || '').substring(0, 10)}</td>
                                <td data-label="Utilidad"
                                    className={`amount ${item.utilidad < 0 ? 'negative' : 'positive'}`}
                                    style={{textAlign: "right"}}>{formatCurrency(item.utilidad)}</td>
                                <td data-label="Margen %"
                                    className={`amount ${item.margenPorcentual < 15 ? 'negative' : 'positive'}`}
                                    style={{textAlign: "right"}}>{Number(item.margenPorcentual || 0).toFixed(1)}%
                                </td>
                                <td data-label="Proveedor" className="table-column-with-button">
                                    <button className="btn-primary btn-sm" onClick={() => openProveedorModal(item)}>
                                        <i className="fa-solid fa-user-pen"></i>
                                    </button>
                                    <span style={{marginLeft: '6px'}}>{item.proveedor}</span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div className="pagination-controls" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '8px'
                        }}>
                            <div className="pagination-info">
                                {startItem}-{endItem}/{totalItems}
                            </div>
                            <div className="pagination-actions" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}>Anterior
                                </button>
                                <span>{currentPage}/{totalPages}</span>
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}>Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
const formatCurrency = (value) => new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
}).format(value || 0);

const formatNumber = (value) => new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value || 0);


// --- Main Dashboard Component ---
const DashboardCompras = ({
                              initialData,
                              historicalData,
                              productMarginAnalysis,
                              startDate,
                              endDate,
                              onStartDateChange,
                              onEndDateChange,
                              onDatesChanged
                          }) => {
    const [sortMain, setSortMain] = useState({key: null, direction: 'asc'});
    const [nestedSort, setNestedSort] = useState({}); // { [groupKey]: { key, direction } }
    const [groupBy, setGroupBy] = useState('proveedor');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    // Modal state for showing historical price analysis per product
    const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const openPriceModal = (productName) => {
        if (!productName) return;
        setSelectedProduct(productName);
        setIsPriceModalOpen(true);
    };
    const closePriceModal = () => {
        setIsPriceModalOpen(false);
        setSelectedProduct(null);
    };

    const formatDateForInput = (date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // El filtrado por búsqueda ahora se hace sobre los datos ya recibidos por la API
    const filteredData = useMemo(() => {
        if (!initialData) return [];
        return initialData.filter(item => {
            const lowerCaseSearch = searchTerm.toLowerCase();
            const matchesSearch = searchTerm === '' ||
                item.proveedor.toLowerCase().includes(lowerCaseSearch) ||
                item.producto.toLowerCase().includes(lowerCaseSearch) ||
                (item.categoria && item.categoria.toLowerCase().includes(lowerCaseSearch));
            return matchesSearch;
        });
    }, [initialData, searchTerm]);

    const processedData = useMemo(() => {
        if (groupBy === 'Todos') {
            return {
                isGrouped: false,
                items: filteredData.sort((a, b) => Math.abs(b.DiferenciaImporte) - Math.abs(a.DiferenciaImporte))
            };
        }

        const groups = filteredData.reduce((acc, item) => {
            const key = item[groupBy];
            if (!acc[key]) {
                acc[key] = {items: [], totalSolicitado: 0, totalRecibido: 0, totalDiferencia: 0, totalAbsoluto: 0};
            }
            acc[key].items.push(item);
            acc[key].totalSolicitado += item.importeSolicitado;
            acc[key].totalRecibido += item.importeRecibido;
            acc[key].totalDiferencia += item.diferenciaImporte;
            acc[key].totalAbsoluto += Math.abs(item.diferenciaImporte);
            return acc;
        }, {});

        return {
            isGrouped: true,
            items: Object.entries(groups)
                .sort(([, a], [, b]) => b.totalAbsoluto - a.totalAbsoluto)
                .map(([key, value]) => ({
                    key, ...value,
                    items: value.items.sort((a, b) => Math.abs(b.DiferenciaImporte) - Math.abs(a.DiferenciaImporte))
                }))
        };
    }, [filteredData, groupBy]);

    // Lógica del sumario actualizada
    const summary = useMemo(() => {
        return filteredData.reduce((acc, item) => {
            acc.totalRecibido += item.importeRecibido;
            acc.totalSolicitado += item.importeSolicitado;
            const diff = item.diferenciaImporte || 0;
            acc.totalDiferencia += diff;
            acc.totalAbsoluto += Math.abs(diff);

            if (diff > 0) {
                acc.totalAbsolutoPositivo += diff;
            } else if (diff < 0) {
                acc.totalAbsolutoNegativo += Math.abs(diff);
            }

            return acc;
        }, {
            totalRecibido: 0,
            totalSolicitado: 0,
            totalDiferencia: 0,
            totalAbsoluto: 0,
            totalAbsolutoPositivo: 0,
            totalAbsolutoNegativo: 0
        });
    }, [filteredData]);

    const toggleGroup = (key) => setExpandedGroups(prev => ({...prev, [key]: !prev[key]}));

    // Sorting helpers
    const parseDate = (v) => {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.getTime();
    };

    const getVal = (obj, key) => {
        if (!obj) return null;
        if (key === 'diferenciaPrecio') {
            const pr = Number(obj?.precioRecibido || 0);
            const ps = Number(obj?.precioSolicitado || 0);
            return pr - ps;
        }
        const val = obj[key];
        if (key === 'fecha') return parseDate(val);
        return val;
    };

    const compare = (a, b, key, direction) => {
        const va = getVal(a, key);
        const vb = getVal(b, key);
        let res = 0;
        if (typeof va === 'number' && typeof vb === 'number') {
            res = va - vb;
        } else if (va instanceof Date && vb instanceof Date) {
            res = va.getTime() - vb.getTime();
        } else if (typeof va === 'string' && typeof vb === 'string') {
            res = va.localeCompare(vb, 'es', {numeric: true, sensitivity: 'base'});
        } else if (va == null && vb != null) {
            res = -1;
        } else if (va != null && vb == null) {
            res = 1;
        } else {
            res = (va > vb) - (va < vb);
        }
        return direction === 'asc' ? res : -res;
    };

    const toggleMainSort = (key) => {
        setSortMain(prev => {
            if (prev.key === key) {
                return {key, direction: prev.direction === 'asc' ? 'desc' : 'asc'};
            }
            return {key, direction: 'asc'};
        });
    };

    const setGroupSort = (groupKey, key) => {
        setNestedSort(prev => {
            const current = prev[groupKey] || {key: null, direction: 'asc'};
            const next = current.key === key ? {key, direction: current.direction === 'asc' ? 'desc' : 'asc'} : {
                key,
                direction: 'asc'
            };
            return {...prev, [groupKey]: next};
        });
    };

    // Build sorted datasets for rendering without mutating processedData
    const sortedDetailItems = useMemo(() => {
        if (!processedData || processedData.isGrouped) return [];
        const items = [...processedData.items];
        if (sortMain.key) {
            items.sort((a, b) => compare(a, b, sortMain.key, sortMain.direction));
        }
        return items;
    }, [processedData, sortMain]);

    const sortedGroups = useMemo(() => {
        if (!processedData || !processedData.isGrouped) return [];
        const groups = [...processedData.items];
        if (sortMain.key) {
            groups.sort((a, b) => compare(a, b, sortMain.key, sortMain.direction));
        }
        return groups;
    }, [processedData, sortMain]);

    const getSortedGroupItems = (group) => {
        const sortCfg = nestedSort[group.key];
        const items = [...group.items];
        if (sortCfg && sortCfg.key) {
            items.sort((a, b) => compare(a, b, sortCfg.key, sortCfg.direction));
        }
        return items;
    };

    const SortIcon = ({active, direction}) => (
        <span className="sort-icon" aria-hidden="true">{active ? (direction === 'asc' ? '▲' : '▼') : ''}</span>
    );

    return (
        <div className="dashboard-container">
            <h1 className="main-title">Dashboard de Análisis de Compras</h1>

            <div className="date-picker-wrapper">
                <input type="date" value={formatDateForInput(startDate)} onChange={(e) => {
                    const [y, m, d] = e.target.value.split('-').map(Number);
                    onStartDateChange(new Date(y, m - 1, d, 0, 0, 0, 0));
                }}/>
                <input type="date" value={formatDateForInput(endDate)} onChange={(e) => {
                    const [y, m, d] = e.target.value.split('-').map(Number);
                    onEndDateChange(new Date(y, m - 1, d, 23, 59, 59, 999));
                }}/>
                <button
                    onClick={() => onDatesChanged && onDatesChanged(new RangoFechas(startDate, endDate))}>Cargar
                </button>
            </div>

            <AnalysisSection summary={summary} processedData={processedData} filteredData={filteredData}
                             groupBy={groupBy}/>

            <div className="summary-grid">
                {/* Orden de cards actualizado */}
                <SummaryCard title="Gasto Presupuestado" value={formatCurrency(summary.totalSolicitado)}/>
                <SummaryCard title="Gasto Real" value={formatCurrency(summary.totalRecibido)}/>
                <SummaryCard title="Diferencia Neta" value={formatCurrency(summary.totalDiferencia)}
                             isBad={summary.totalDiferencia > 100}/>
                <VariationSummaryCard
                    title="Variación Absoluta Total"
                    total={formatCurrency(summary.totalAbsoluto)}
                    positive={formatCurrency(summary.totalAbsolutoPositivo)}
                    negative={formatCurrency(summary.totalAbsolutoNegativo)}
                    isBad={true}
                    tooltip="Suma de todas las diferencias en valor absoluto. Indica la inestabilidad del proceso."
                />
            </div>

            <div className="controls-container">

                <div>
                    <button onClick={() => setGroupBy('proveedor')}
                            className={groupBy === 'proveedor' ? 'active' : ''}>Proveedor
                    </button>
                    <button onClick={() => setGroupBy('producto')}
                            className={groupBy === 'producto' ? 'active' : ''}>Producto
                    </button>
                    <button onClick={() => setGroupBy('Todos')} className={groupBy === 'Todos' ? 'active' : ''}>Todos
                    </button>
                </div>
                <div className="search-input">
                    <input type="text" placeholder="Buscar por Proveedor, Producto o Categoría..." value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}/>
                    {searchTerm && (
                        <button
                            type="button"
                            className="clear-btn"
                            aria-label="Limpiar búsqueda"
                            onClick={() => setSearchTerm('')}
                            title="Limpiar"
                        >
                            ×
                        </button>
                    )}
                </div>

            </div>

            <div className="main-content-grid">
                <div className="report-table-container">
                    <table className="report-table">
                        <thead>
                        {groupBy === 'Todos' ? (
                            <tr>
                                <th className="sortable" onClick={() => toggleMainSort('fecha')}>Fecha <SortIcon
                                    active={sortMain.key === 'fecha'} direction={sortMain.direction}/></th>
                                <th className="sortable" onClick={() => toggleMainSort('producto')}>Producto <SortIcon
                                    active={sortMain.key === 'producto'} direction={sortMain.direction}/></th>
                                <th className="sortable" onClick={() => toggleMainSort('proveedor')}>Proveedor <SortIcon
                                    active={sortMain.key === 'proveedor'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('cantidadSolicitada')}>Cant. Sol. <SortIcon
                                    active={sortMain.key === 'cantidadSolicitada'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('cantidadRecibida')}>Cant. Rec. <SortIcon
                                    active={sortMain.key === 'cantidadRecibida'} direction={sortMain.direction}/></th>
                                {/* Nuevas columnas de kilos y precio por kilo */}
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('kilosSolicitados')}>Kg. Sol. <SortIcon
                                    active={sortMain.key === 'kilosSolicitados'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('kilosRecibidos')}>Kg. Rec. <SortIcon
                                    active={sortMain.key === 'kilosRecibidos'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('precioPorKiloSolicitado')}>P/Kg Sol. <SortIcon
                                    active={sortMain.key === 'precioPorKiloSolicitado'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('precioPorKiloRecibido')}>P/Kg Real <SortIcon
                                    active={sortMain.key === 'precioPorKiloRecibido'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('diferenciaCantidad')}>Dif. Cant. <SortIcon
                                    active={sortMain.key === 'diferenciaCantidad'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('precioSolicitado')}>P.U. Sol. <SortIcon
                                    active={sortMain.key === 'precioSolicitado'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('precioRecibido')}>P.U. Real <SortIcon
                                    active={sortMain.key === 'precioRecibido'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('diferenciaPrecio')}>Dif. Precio <SortIcon
                                    active={sortMain.key === 'diferenciaPrecio'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('importeSolicitado')}>Importe Sol. <SortIcon
                                    active={sortMain.key === 'importeSolicitado'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('importeRecibido')}>Importe Rec. <SortIcon
                                    active={sortMain.key === 'importeRecibido'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('diferenciaImporte')}>Dif. Importe <SortIcon
                                    active={sortMain.key === 'diferenciaImporte'} direction={sortMain.direction}/></th>
                            </tr>
                        ) : (
                            <tr>
                                <th className="sortable" onClick={() => toggleMainSort('key')}>{groupBy} <SortIcon
                                    active={sortMain.key === 'key'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('totalSolicitado')}>Total Solicitado <SortIcon
                                    active={sortMain.key === 'totalSolicitado'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('totalRecibido')}>Total Recibido <SortIcon
                                    active={sortMain.key === 'totalRecibido'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('totalDiferencia')}>Diferencia Total <SortIcon
                                    active={sortMain.key === 'totalDiferencia'} direction={sortMain.direction}/></th>
                                <th className="sortable" style={{textAlign: "right"}}
                                    onClick={() => toggleMainSort('totalAbsoluto')}>Variación Absoluta <SortIcon
                                    active={sortMain.key === 'totalAbsoluto'} direction={sortMain.direction}/></th>
                            </tr>
                        )}
                        </thead>
                        <tbody>
                        {!processedData.isGrouped ? (
                            sortedDetailItems.map((item, index) => (
                                <tr key={`${item.id}-${index}`} className="detail-row" onClick={() => openPriceModal(item.producto)} style={{cursor: 'pointer'}}>
                                    <td data-label="Fecha">{item.fecha}</td>
                                    <td data-label="Producto">{item.producto}</td>
                                    <td data-label="Proveedor">{item.proveedor}</td>
                                    <td data-label="Cant. Sol." className="amount">{item.cantidadSolicitada}</td>
                                    <td data-label="Cant. Rec." className="amount">{item.cantidadRecibida}</td>
                                    {/* Nuevas columnas de kilos y precio por kilo */}
                                    <td data-label="Kg. Sol." className="amount">{item.kilosSolicitados}</td>
                                    <td data-label="Kg. Rec." className="amount">{item.kilosRecibidos}</td>
                                    <td data-label="P/Kg Sol." className="amount">{formatCurrency(item.kilosSolicitados > 0 ? item.precioPorKiloSolicitado : 0)}</td>
                                    <td data-label="P/Kg Real" className="amount">{formatCurrency(item.kilosRecibidos > 0 ? item.precioPorKiloRecibido : 0)}</td>
                                    <td data-label="Dif. Cant."
                                        className={`amount ${item.diferenciaCantidad > 0 ? 'negative' : item.diferenciaCantidad < 0 ? 'positive' : ''}`}>{item.diferenciaCantidad}</td>
                                    <td data-label="P.U. Sol."
                                        className="amount">{formatCurrency(item.cantidadSolicitada > 0 ? item.precioSolicitado : 0)}</td>
                                    <td data-label="P.U. Real"
                                        className="amount">{formatCurrency(item.cantidadRecibida > 0 ? item.precioRecibido : 0)}</td>
                                    <td data-label="Dif. Precio"
                                        className={`amount ${(item.precioRecibido - item.precioSolicitado) > 0 ? 'negative' : (item.precioRecibido - item.precioSolicitado) < 0 ? 'positive' : ''}`}>
                                        {formatCurrency((item.precioRecibido - item.precioSolicitado) || 0)}
                                    </td>
                                    <td data-label="Importe Sol."
                                        className="amount">{formatCurrency(item.importeSolicitado)}</td>
                                    <td data-label="Importe Rec."
                                        className="amount">{formatCurrency(item.importeRecibido)}</td>
                                    <td data-label="Dif. Importe"
                                        className={`amount ${item.diferenciaImporte > 0 ? 'negative' : item.diferenciaImporte < 0 ? 'positive' : ''}`}>
                                        {formatCurrency(item.diferenciaImporte)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            sortedGroups.map(group => (
                                <React.Fragment key={group.key}>
                                    <tr className="group-row" onClick={() => toggleGroup(group.key)}>
                                        <td data-label={groupBy}>
                                            <span>{expandedGroups[group.key] ? '▼' : '►'}</span> {group.key}</td>
                                        <td data-label="Total Solicitado"
                                            className="amount">{formatCurrency(group.totalSolicitado)}</td>
                                        <td data-label="Total Recibido"
                                            className="amount">{formatCurrency(group.totalRecibido)}</td>
                                        <td data-label="Diferencia Total"
                                            className={`amount ${group.totalDiferencia > 0 ? 'negative' : group.totalDiferencia < 0 ? 'positive' : ''}`}>
                                            {formatCurrency(group.totalDiferencia)}</td>
                                        <td data-label="Variación Absoluta"
                                            className="amount negative">{formatCurrency(group.totalAbsoluto)}</td>
                                    </tr>
                                    {expandedGroups[group.key] && (
                                        <tr className="nested-table-row">
                                            <td colSpan="5">
                                                <table className="nested-table">
                                                    <thead>
                                                    <tr>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'fecha')}>Fecha <SortIcon
                                                            active={(nestedSort[group.key]?.key) === 'fecha'}
                                                            direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, (groupBy === 'proveedor' ? 'producto' : 'proveedor'))}>
                                                            {groupBy === 'proveedor' ? 'producto' : 'proveedor'}
                                                            <SortIcon
                                                                active={(nestedSort[group.key]?.key) === (groupBy === 'proveedor' ? 'producto' : 'proveedor')}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'cantidadSolicitada')}>Cant.
                                                            Sol. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'cantidadSolicitada'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'cantidadRecibida')}>Cant.
                                                            Rec. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'cantidadRecibida'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        {/* Nuevas columnas de kilos y precio por kilo en vista agrupada */}
                                                        <th className="sortable" style={{textAlign: "right"}}
                                                            onClick={() => setGroupSort(group.key, 'kilosSolicitados')}>Kg. Sol. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'kilosSolicitados'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable" style={{textAlign: "right"}}
                                                            onClick={() => setGroupSort(group.key, 'kilosRecibidos')}>Kg. Rec. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'kilosRecibidos'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable" style={{textAlign: "right"}}
                                                            onClick={() => setGroupSort(group.key, 'precioPorKiloSolicitado')}>P/Kg Sol. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'precioPorKiloSolicitado'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable" style={{textAlign: "right"}}
                                                            onClick={() => setGroupSort(group.key, 'precioPorKiloRecibido')}>P/Kg Real <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'precioPorKiloRecibido'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'diferenciaCantidad')}>Dif.
                                                            Cant. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'diferenciaCantidad'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'precioSolicitado')}>P.U.
                                                            Sol. <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'precioSolicitado'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'precioRecibido')}>P.U.
                                                            Real <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'precioRecibido'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/>
                                                        </th>
                                                        <th className="sortable" style={{textAlign: "right"}}
                                                            onClick={() => setGroupSort(group.key, 'diferenciaPrecio')}>Dif. Precio <SortIcon
                                                                active={(nestedSort[group.key]?.key) === 'diferenciaPrecio'}
                                                                direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'importeSolicitado')}>Solicitado <SortIcon
                                                            active={(nestedSort[group.key]?.key) === 'importeSolicitado'}
                                                            direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'importeRecibido')}>Recibido <SortIcon
                                                            active={(nestedSort[group.key]?.key) === 'importeRecibido'}
                                                            direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                        <th className="sortable"
                                                            onClick={() => setGroupSort(group.key, 'diferenciaImporte')}>Diferencia <SortIcon
                                                            active={(nestedSort[group.key]?.key) === 'diferenciaImporte'}
                                                            direction={nestedSort[group.key]?.direction || 'asc'}/></th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {getSortedGroupItems(group).map((item, index) => (
                                                        <tr key={`${item.id}-${index}`} onClick={() => openPriceModal(item.producto)} style={{cursor: 'pointer'}}>
                                                            <td data-label="Fecha">{item.fecha}</td>
                                                            <td data-label={groupBy === 'proveedor' ? 'producto' : 'proveedor'}>{groupBy === 'proveedor' ? item.producto : item.proveedor}</td>
                                                            <td data-label="Cant. Sol."
                                                                className="amount">{item.cantidadSolicitada}</td>
                                                            <td data-label="Cant. Rec."
                                                                className="amount">{item.cantidadRecibida}</td>
                                                            {/* Nuevas columnas de kilos y precio por kilo en filas agrupadas */}
                                                            <td data-label="Kg. Sol." className="amount">{item.kilosSolicitados}</td>
                                                            <td data-label="Kg. Rec." className="amount">{item.kilosRecibidos}</td>
                                                            <td data-label="P/Kg Sol." className="amount">{formatCurrency(item.kilosSolicitados > 0 ? item.precioPorKiloSolicitado : 0)}</td>
                                                            <td data-label="P/Kg Real" className="amount">{formatCurrency(item.kilosRecibidos > 0 ? item.precioPorKiloRecibido : 0)}</td>
                                                            <td data-label="Dif. Cant."
                                                                className={`amount ${item.diferenciaCantidad > 0 ? 'negative' : item.diferenciaCantidad < 0 ? 'positive' : ''}`}>{item.diferenciaCantidad}</td>
                                                            <td data-label="P.U. Sol."
                                                                className="amount">{formatCurrency(item.cantidadSolicitada > 0 ? item.precioSolicitado : 0)}</td>
                                                            <td data-label="P.U. Real"
                                                                className="amount">{formatCurrency(item.cantidadRecibida > 0 ? item.precioRecibido : 0)}</td>
                                                            <td data-label="Dif. Precio"
                                                                className={`amount ${(item.precioRecibido - item.precioSolicitado) > 0 ? 'negative' : (item.precioRecibido - item.precioSolicitado) < 0 ? 'positive' : ''}`}>
                                                                {formatCurrency((item.precioRecibido - item.precioSolicitado) || 0)}
                                                            </td>
                                                            <td data-label="Solicitado"
                                                                className="amount">{formatCurrency(item.importeSolicitado)}</td>
                                                            <td data-label="Recibido"
                                                                className="amount">{formatCurrency(item.importeRecibido)}</td>
                                                            <td data-label="Diferencia"
                                                                className={`amount ${item.diferenciaImporte > 0 ? 'negative' : item.diferenciaImporte < 0 ? 'positive' : ''}`}>
                                                                {formatCurrency(item.diferenciaImporte)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
                <HistoricalPriceAnalysis currentData={filteredData} historicalData={historicalData}/>
                <ProductMarginAnalysis productMarginData={productMarginAnalysis} searchTerm={searchTerm}/>
            </div>

            {isPriceModalOpen && selectedProduct && (
                <div className="modal-overlay" onClick={closePriceModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h3>Análisis de margen del producto: {selectedProduct}</h3>
                            <button onClick={closePriceModal}>Cerrar</button>
                        </div>
                        <ProductMarginAnalysis
                            productMarginData={(productMarginAnalysis || []).filter(p => p.producto === selectedProduct)}
                            searchTerm={selectedProduct}
                        />
                        <div style={{ marginTop: '16px' }}>
                            <HistoricalPriceAnalysis
                                currentData={filteredData.filter(i => i.producto === selectedProduct)}
                                historicalData={historicalData.filter(h => h.producto === selectedProduct)}
                            />
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};


export default DashboardCompras;
